import { useBasePath } from '../../../lib/use-base-path';
import { useTheme } from '../../../lib/theme-context';
import styles from './EmptyHero.module.css';

/**
 * The two empty-state illustrations, as `<img>` elements.
 *
 * ⚠️ They were CSS `background-image`s until 2026-09-10, painted off `--hero-img` custom
 * properties this file set inline, and on production BOTH of them rendered as blank space —
 * the reported "the empty states look broken". The mechanism is worth keeping written down,
 * because nothing about it is visible in dev:
 *
 *   next.config.js points `assetPrefix` at the parastorage CDN, so the CSS-module stylesheet
 *   is served from `https://static.parastorage.com/services/orion-web/<hash>/_next/static/css/…`.
 *   A `url()` inside a stylesheet resolves against THAT stylesheet's base URL, not the
 *   document's — so the root-relative `/illustrations/home-empty-hero.png` resolved to
 *   `https://static.parastorage.com/illustrations/home-empty-hero.png`, which does not exist.
 *   `public/` is served by the POD (symphony.wix.com), never uploaded to the CDN, so a
 *   `public/` asset can never be reached from a rule that lives in a CSS module.
 *
 * A broken `background-image` paints nothing and logs nothing, which is why this shipped and
 * survived a round of "fix the illustration URLs" (#8250 — it corrected the mount prefix, which
 * was a real but different bug). An `<img src>` resolves against the DOCUMENT, so it is correct
 * on every mount and every host by construction, and a missing file is a visible network error.
 * That is also why `PackAvatarCluster` — the other `public/illustrations` consumer — was never
 * affected: it was always an `<img>`.
 *
 * **Do not move these back into the stylesheet**, and do not put any other `public/` asset in a
 * `url()` inside a `.module.css`. `test/empty-states.test.ts` fails on it.
 *
 * The light/dark cut is picked from `useTheme()` — the app's own resolved theme, the same value
 * that stamps `data-theme` on `<html>` — rather than `prefers-color-scheme`, which is not the
 * app's preference (an explicit "light" pick on a dark OS must stay light).
 */
function useHeroSrc(): (file: string) => string {
  const basePath = useBasePath();
  return (file) => `${basePath}/illustrations/${file}`;
}

/**
 * The Your-Agents empty illustration: the orbit constellation carrying its own
 * "N Agents · M Skills" pill. A static export (not the live `TeamGenomeCard`) because
 * the state it illustrates is precisely the one with no agents to plot — the same
 * reason native uses a static `AgentsEmptyHero`. Byte-identical to the iOS/Android asset.
 */
export function AgentsEmptyHero() {
  const { theme } = useTheme();
  const src = useHeroSrc();
  return (
    <img
      alt=""
      aria-hidden
      className={styles.agents}
      src={src(theme === 'dark' ? 'agents-empty-hero-dark.png' : 'agents-empty-hero.png')}
    />
  );
}

/**
 * The home empty illustration: the conveyor of dashboard-preview cards, in the composition each
 * viewport's own frame draws — so `<source media>` fetches exactly one of them.
 *
 * - **Narrow** — the TWO-ROW strip (Figma `9216:101503`), byte-identical to the asset both phones
 *   ship (`HomeEmptyHero` / `home_empty_hero`), so mobile web reads as the phones do. It is the
 *   one with a designed dark cut.
 * - **Wide** — the one-row band the desktop frame draws (Figma `10941:75527`), a ~4720×333 export
 *   that runs the pane's full width and bleeds off both edges (the bleed is `.hero` in
 *   `EmptyDashboard.module.css`).
 *
 * The wide band has **no dark cut**, deliberately rather than by omission: there is no dark
 * desktop frame to export one from, and the artwork is opaque colour tiles on transparency —
 * checked composited on the dark canvas (`--ds-surface-standard-bg`, #1F1F1F) and it reads. The
 * phone strip's dark cut is used on narrow because one exists.
 */
export function HomeEmptyHero() {
  const { theme } = useTheme();
  const src = useHeroSrc();
  return (
    <picture>
      <source media="(min-width: 720px)" srcSet={src('home-empty-hero-wide.png')} />
      <img
        alt=""
        aria-hidden
        className={styles.home}
        src={src(theme === 'dark' ? 'home-empty-hero-dark.png' : 'home-empty-hero.png')}
      />
    </picture>
  );
}
